import { groupBy, noop, sortBy } from "lodash-es";

import { writer } from "@/backend/write/BackendWriter";
import { times } from "@/math/coordinates";
import type { Board } from "@/model/board";
import type { PositionedCard } from "@/model/card";
import type { RelativeCoordinate } from "@/model/coordinates";
import { relativeCoord } from "@/model/coordinates";
import type { Art, Team } from "@/model/session";
import { fullUrl } from "@/router/navigation";
import { cardSize, useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { useCardStore } from "@/store/card";
import { useClientSettingsStore } from "@/store/clientSettings";
import { useSelectionStore } from "@/store/selection";
import { useToastStore } from "@/store/toast";
import { useGlobalI18n } from "@/translations/i18n";
import { ensureResolve } from "@/utils/async/utils";
import { alignCards, arrangeCards, distributeCards, lineupCards, stackCards } from "@/utils/layout";

import { action, ctxAction, defineActions, untrackedAction } from "./actions";
import type { CardCreateProps } from "./addCard";
import { cardActions } from "./cardActions";
import { internalActions } from "./internalActions";
import { linkActions } from "./linkActions";
import { navigationActions } from "./navigationActions";
import type { ActionContext } from "./types";

interface MirroringState {
  id: string;
  teamId: string | null;
  mirroredId: string | undefined;
}

const { t } = useGlobalI18n();

export const selectionActions = defineActions("selection", {
  alignHorizontal: ctxAction(alignHorizontal, {
    name: /*$t*/ "action.alignHorizontal",
    icon: "selection/align-horizontal",
  }),
  alignVertical: ctxAction(alignVertical, {
    name: /*$t*/ "action.alignVertical",
    icon: "selection/align-vertical",
  }),
  distributeHorizontal: ctxAction(distributeHorizontal, {
    name: /*$t*/ "action.distributeHorizontal",
    icon: "selection/distribute-horizontal",
  }),
  distributeVertical: ctxAction(distributeVertical, {
    name: /*$t*/ "action.distributeVertical",
    icon: "selection/distribute-vertical",
  }),
  lineup: ctxAction(lineup, { name: /*$t*/ "action.lineup", icon: "selection/grid" }),
  arrange: ctxAction(arrange, { name: /*$t*/ "action.arrange", icon: "selection/arrange" }),
  stack: ctxAction(stack, { name: /*$t*/ "action.stack", icon: "selection/stack" }),
  organizeStickies: action(noop, {
    name: /*$t*/ "actionMenu.organize",
    icon: "selection/grid",
  }),
  link: ctxAction(link),
  openMirrorModal: untrackedAction((ctx) => navigationActions.openModal(ctx, "MirrorModal"), {
    name: /*$t*/ "action.mirrorMultiple",
    icon: "general/mirror",
  }),
  mirror: ctxAction(mirror),
  openTeamModal: untrackedAction((ctx) => navigationActions.openModal(ctx, "TeamModal"), {
    name: /*$t*/ "action.team",
    icon: "persona/team",
  }),
  setTeam: ctxAction(setTeam),
  openArtModal: untrackedAction((ctx) => navigationActions.openModal(ctx, "ArtModal"), {
    name: /*$t*/ "action.art",
    icon: "general/art",
  }),
  setArt: ctxAction(setArt),
  paste: untrackedAction(paste),
  duplicate: ctxAction(duplicate),
  share: action(
    () => {
      void navigator.clipboard.writeText(
        fullUrl({ stickyId: useBoardStore().selectedStickyIds.join(",") }),
      );
      useToastStore().show(/*$t*/ "message.stickyLinkCopied", {
        messageParams: { count: useBoardStore().selectedStickyIds.length },
      });
    },
    { name: /*$t*/ "action.share", label: /*$t*/ "label.actionMenu.share", icon: "general/share" },
  ),
  undo: action(() => useSelectionStore().undo(), { icon: "arrow/undo" }),
});

function alignHorizontal(ctx: ActionContext) {
  return useSelectionStore().execute({
    name: t(ctx.data.name),
    execute() {
      const cards = useBoardStore().selectedStickies;
      void setCardPositions(alignCards(cards, "horizontal"));
    },
    saveState: saveCardPositions,
    resetToState: resetCardPositions,
  });
}

function alignVertical(ctx: ActionContext) {
  return useSelectionStore().execute({
    name: t(ctx.data.name),
    execute() {
      const cards = useBoardStore().selectedStickies;
      void setCardPositions(alignCards(cards, "vertical"));
    },
    saveState: saveCardPositions,
    resetToState: resetCardPositions,
  });
}

function distributeHorizontal(ctx: ActionContext) {
  return useSelectionStore().execute({
    name: t(ctx.data.name),
    execute() {
      const cards = useBoardStore().selectedStickies;
      void setCardPositions(distributeCards(alignCards(cards, "horizontal"), "horizontal"));
    },
    saveState: saveCardPositions,
    resetToState: resetCardPositions,
  });
}

function distributeVertical(ctx: ActionContext) {
  return useSelectionStore().execute({
    name: t(ctx.data.name),
    execute() {
      const cards = useBoardStore().selectedStickies;
      void setCardPositions(distributeCards(alignCards(cards, "vertical"), "vertical"));
    },
    saveState: saveCardPositions,
    resetToState: resetCardPositions,
  });
}

function lineup(ctx: ActionContext) {
  return useSelectionStore().execute({
    name: t(ctx.data.name),
    execute() {
      const cards = lineupCards(
        useBoardStore().selectedStickies,
        cardSize(),
        0.01 * useClientSettingsStore().initialLineupDistance,
        (pos) => useBoardStore().boardLocation(pos).bounds,
      );
      void setCardPositions(cards);
    },
    saveState: saveCardPositions,
    resetToState: resetCardPositions,
  });
}

function stack(ctx: ActionContext) {
  const dist = relativeCoord(
    useClientSettingsStore().stackDistanceX,
    useClientSettingsStore().stackDistanceY,
  );
  return useSelectionStore().execute({
    name: t(ctx.data.name),
    execute() {
      const cards = stackCards(
        useBoardStore().selectedStickies,
        cardSize(),
        times(dist, 0.01),
        (pos) => useBoardStore().boardLocation(pos).bounds,
      );
      void setCardPositions(cards, sortByDirection(dist));
    },
    saveState: saveCardPositions,
    resetToState: resetCardPositions,
  });
}

function arrange(ctx: ActionContext) {
  return useSelectionStore().execute({
    name: t(ctx.data.name),
    execute() {
      const store = useBoardStore();
      const cardsByLoc = groupBy(store.selectedStickies, (card) =>
        store.boardLocation(card.meta.pos).id(),
      );
      if (Object.keys(cardsByLoc).length > 1) {
        return useToastStore().show(/*$t*/ "message.multipleRegionsSelected");
      }
      const cards = arrangeCards(
        store.currentBoard(),
        store.selectedStickies,
        store.boardLocationById(Object.keys(cardsByLoc)[0]).bounds,
      );
      void setCardPositions(cards);
    },
    saveState: () => {
      const cards = saveCardPositions();
      return {
        factor: useBoardStore().currentBoard().cardSize.factor,
        cards,
        length: cards.length,
      };
    },
    resetToState: ({ factor, cards }) => {
      useBoardsStore().setCardSize({ boardId: useBoardStore().currentBoard().id, factor });
      return resetCardPositions(cards);
    },
  });
}

function link(ctx: ActionContext) {
  const stateToBeSaved = useBoardStore().selectedStickies.map((card) => ({
    id: card.data.id,
    links: [...card.data.temporaryLinks],
    objectives: [...card.data.objectives],
  }));
  void useSelectionStore().execute({
    name: cardActions.openLinkModal.data.name,
    execute() {
      // linking is done in LinkModal
    },
    saveState: () => stateToBeSaved,
    resetToState(cardStates) {
      for (const cardState of cardStates) {
        const card = useCardStore().cards[cardState.id];
        card.temporaryLinks.forEach((newLink) => {
          if (!cardState.links.some((oldLink) => oldLink.id === newLink.id)) {
            void linkActions.remove(ctx, newLink.id, newLink);
          }
        });
        cardState.links.forEach((oldLink) => {
          if (!card.temporaryLinks.some((newLink) => oldLink.id === newLink.id)) {
            void linkActions.add(ctx, { fromId: oldLink.from, toId: oldLink.to });
          }
        });
        card.objectives.forEach((newObj) => {
          if (!cardState.objectives.some((oldObj) => oldObj.id === newObj.id)) {
            void linkActions.removeObjective(
              ctx,
              card.id,
              useBoardStore().currentBoard().id,
              newObj.id,
            );
          }
        });
        cardState.objectives.forEach((oldObj) => {
          if (!card.objectives.some((newObj) => oldObj.id === newObj.id)) {
            void linkActions.addObjective(
              ctx,
              card.id,
              useBoardStore().currentBoard().id,
              oldObj.id,
            );
          }
        });
      }
    },
  });
}

function mirror(ctx: ActionContext, board: Board) {
  void useSelectionStore().execute({
    name: selectionActions.openMirrorModal.data.name,
    execute(cardStates) {
      ctx.track({});
      cardStates.forEach((cardState) => {
        void cardActions
          .mirror(ctx, cardState.id, cardState.teamId, board)
          .then((mirroredId) => (cardState.mirroredId = mirroredId));
      });
    },
    saveState: () =>
      useBoardStore().selectedStickies.map(
        (card): MirroringState => ({
          id: card.data.id,
          teamId: card.data.teamId,
          mirroredId: undefined,
        }),
      ),
    resetToState(cardStates) {
      cardStates.forEach((cardState) => {
        if (cardState.mirroredId) {
          cardActions.delete(ctx, cardState.mirroredId, board.id);
        }
        if (!cardState.teamId) {
          internalActions.setTeam(cardState.id, null);
        }
      });
    },
  });
}

function setTeam(ctx: ActionContext, team: Team) {
  void useSelectionStore().execute({
    name: selectionActions.setTeam.data.name,
    execute() {
      ctx.track({});
      useBoardStore().selectedStickyIds.forEach((id) => {
        cardActions.setTeam(ctx, id, team.id);
      });
    },
    saveState: () =>
      useBoardStore().selectedStickies.map((card) => ({
        id: card.data.id,
        teamId: card.data.teamId,
        iterationId: card.data.iterationId,
      })),
    resetToState(cardStates) {
      cardStates.forEach((cardState) => {
        internalActions.setTeam(cardState.id, cardState.teamId);
        internalActions.setIteration(cardState.id, cardState.iterationId);
      });
    },
  });
}

function setArt(ctx: ActionContext, art: Art) {
  void useSelectionStore().execute({
    name: selectionActions.setArt.data.name,
    execute() {
      ctx.track({});
      useBoardStore().selectedStickyIds.forEach((id) => {
        cardActions.setArt(ctx, id, art.id);
      });
    },
    saveState: () =>
      useBoardStore().selectedStickies.map((card) => ({
        id: card.data.id,
        artId: card.data.artId,
      })),
    resetToState(cardStates) {
      cardStates.forEach((cardState) => {
        internalActions.setArt(cardState.id, cardState.artId);
      });
    },
  });
}

function paste(ctx: ActionContext, cards: CardCreateProps[]) {
  void useSelectionStore().execute({
    name: cardActions.pasteMultiple.data.name,
    async execute(state) {
      state.push(...(await cardActions.pasteMultiple(ctx, cards)));
    },
    saveState: () => new Array<string>(),
    resetToState(cardStates) {
      cardStates.forEach((cardState) => {
        cardActions.delete(ctx, cardState, useBoardStore().currentBoard().id);
      });
    },
  });
}

function duplicate(ctx: ActionContext, cardIds: string[]) {
  void useSelectionStore().execute({
    name: cardActions.duplicate.data.name,
    execute() {
      //cards are already duplicated, just register the action for undo
    },
    saveState: () => cardIds,
    resetToState(cardStates) {
      cardStates.forEach((cardState) => {
        cardActions.delete(ctx, cardState, useBoardStore().currentBoard().id);
      });
    },
  });
}

function saveCardPositions(): PositionedCard[] {
  return useBoardStore().selectedStickies.map((card) => ({
    data: { id: card.data.id },
    meta: { pos: { ...card.meta.pos }, zIndex: card.meta.zIndex },
  }));
}

type Sorter = (card: PositionedCard) => number;
const sortByPosition: Sorter[] = [(card) => card.meta.pos.y, (card) => card.meta.pos.x];
function sortByDirection(dir: RelativeCoordinate): Sorter {
  return Math.abs(dir.x) > 0
    ? (card) => card.meta.pos.x * Math.sign(dir.x)
    : (card) => card.meta.pos.y * Math.sign(dir.y);
}

export function setCardPositions(
  cards: PositionedCard[],
  sorter: Sorter | Sorter[] = sortByPosition,
) {
  const boardId = useBoardStore().currentBoard().id;
  return ensureResolve((resolver) => {
    const sorted = sortBy(cards, sorter);
    sorted.forEach((card, index) => {
      const pos = { id: card.data.id, boardId, ...card.meta.pos };
      useBoardsStore().setCardPos(pos, index === cards.length - 1 ? resolver() : noop);
      useBoardsStore().cardToFront({ id: card.data.id, boardId });
    });
    sorted.forEach((card) => {
      internalActions.moveCard(boardId, card.data.id, card.meta.pos, true);
    });
    writer.frontCard(
      boardId,
      sorted.map((card) => card.data.id),
    );
  });
}

function resetCardPositions(cardStates: PositionedCard[]) {
  const boardId = useBoardStore().currentBoard().id;
  return ensureResolve((resolver) => {
    cardStates.forEach((card, index) => {
      const pos = { id: card.data.id, boardId, ...card.meta.pos };
      useBoardsStore().setCardPos(pos, index === cardStates.length - 1 ? resolver() : noop);
      useBoardsStore().cardToFront({ id: card.data.id, boardId, zIndex: card.meta.zIndex });
      internalActions.moveCard(boardId, card.data.id, card.meta.pos, true);
      writer.alterCard(boardId, card.data.id, { zIndex: card.meta.zIndex });
    });
  });
}
